import { api } from '@/lib/api'
import { resolveReportPeriodBounds } from '@/lib/reports/filters'

export {
  REPORT_COMPARE_OPTIONS,
  REPORT_PERIOD_PRESETS as REPORT_PERIOD_OPTIONS,
  resolveComparePeriodBounds,
  resolveReportPeriodBounds,
} from '@/lib/reports/filters'

/**
 * @param {{ from?: string, to?: string }} [options]
 */
export async function fetchGrnPurchasesReport(options = {}) {
  const params = {}
  if (options.from) params.from = options.from
  if (options.to) params.to = options.to
  const { data } = await api.get('/reports/grn-purchases', { params })
  return data.data
}

/**
 * @param {{ from?: string, to?: string }} [options]
 */
export async function fetchConsumablesUsageReport(options = {}) {
  const params = {}
  if (options.from) params.from = options.from
  if (options.to) params.to = options.to
  const { data } = await api.get('/reports/consumables-usage', { params })
  return data.data
}

/**
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function fetchPackageSalesReport(options = {}) {
  const { data } = await api.get('/reports/package-sales', { params: buildReportParams(options) })
  return data.data
}

/**
 * @param {'csv' | 'pdf'} format
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function exportPackageSalesReport(format, options = {}) {
  const params = { ...buildReportParams(options), format }

  const response = await api.get('/reports/package-sales/export', {
    params,
    responseType: 'blob',
  })

  const disposition = response.headers['content-disposition'] ?? ''
  const match = disposition.match(/filename="?([^";]+)"?/)
  const filename = match?.[1] ?? `package-sales.${format}`

  downloadBlob(filename, response.data)
}

/**
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function fetchPackageRedemptionAuditReport(options = {}) {
  const { data } = await api.get('/reports/package-redemptions', { params: buildReportParams(options) })
  return data.data
}

/**
 * @param {'csv' | 'pdf'} format
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function exportPackageRedemptionAuditReport(format, options = {}) {
  const params = { ...buildReportParams(options), format }

  const response = await api.get('/reports/package-redemptions/export', {
    params,
    responseType: 'blob',
  })

  const disposition = response.headers['content-disposition'] ?? ''
  const match = disposition.match(/filename="?([^";]+)"?/)
  const filename = match?.[1] ?? `package-redemptions.${format}`

  downloadBlob(filename, response.data)
}

/**
 * @param {number} [days=30]
 */
export async function fetchExpiringPackagesReport(days = 30) {
  const { data } = await api.get('/reports/expiring-packages', {
    params: { days },
  })
  return data.data
}

/**
 * @param {'csv' | 'pdf'} format
 * @param {number} [days=30]
 */
export async function exportExpiringPackagesReport(format, days = 30) {
  const response = await api.get('/reports/expiring-packages/export', {
    params: { format, days },
    responseType: 'blob',
  })

  const disposition = response.headers['content-disposition'] ?? ''
  const match = disposition.match(/filename="?([^";]+)"?/)
  const filename = match?.[1] ?? `expiring-packages.${format}`

  downloadBlob(filename, response.data)
}

/**
 * @param {{ period?: string, from?: string, to?: string, compare?: string, stylistId?: number | null }} [options]
 */
export async function fetchStylistPerformanceReport(options = {}) {
  const params = buildReportParams(options)
  if (options.stylistId) params.stylist_id = options.stylistId

  const { data } = await api.get('/reports/stylist-performance', { params })
  return data.data
}

/**
 * @param {'csv' | 'pdf'} format
 * @param {{ period?: string, from?: string, to?: string, compare?: string, stylistId?: number | null }} [options]
 */
export async function exportStylistPerformanceReport(format, options = {}) {
  const params = { ...buildReportParams(options), format }
  if (options.stylistId) params.stylist_id = options.stylistId

  const response = await api.get('/reports/stylist-performance/export', {
    params,
    responseType: 'blob',
  })

  const disposition = response.headers['content-disposition'] ?? ''
  const match = disposition.match(/filename="?([^";]+)"?/)
  const filename = match?.[1] ?? `stylist-performance.${format}`

  downloadBlob(filename, response.data)
}

/**
 * @param {{ period?: string, from?: string, to?: string, compare?: string, stylistId?: number | null }} [options]
 */
export async function fetchCommissionReport(options = {}) {
  const params = buildReportParams(options)
  if (options.stylistId) params.stylist_id = options.stylistId

  const { data } = await api.get('/reports/commissions', { params })
  return data.data
}

/**
 * @param {'csv' | 'pdf'} format
 * @param {{ period?: string, from?: string, to?: string, compare?: string, stylistId?: number | null }} [options]
 */
export async function exportCommissionReport(format, options = {}) {
  const params = { ...buildReportParams(options), format }
  if (options.stylistId) params.stylist_id = options.stylistId

  const response = await api.get('/reports/commissions/export', {
    params,
    responseType: 'blob',
  })

  const disposition = response.headers['content-disposition'] ?? ''
  const match = disposition.match(/filename="?([^";]+)"?/)
  const filename = match?.[1] ?? `commissions.${format}`

  downloadBlob(filename, response.data)
}

export async function fetchReportCatalog() {
  const { data } = await api.get('/reports/catalog')
  return data.data
}

/**
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function fetchDailySalesReport(options = {}) {
  const { data } = await api.get('/reports/daily-sales', { params: buildReportParams(options) })
  return data.data
}

/**
 * @param {'csv' | 'pdf'} format
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function exportDailySalesReport(format, options = {}) {
  const params = { ...buildReportParams(options), format }

  const response = await api.get('/reports/daily-sales/export', {
    params,
    responseType: 'blob',
  })

  const disposition = response.headers['content-disposition'] ?? ''
  const match = disposition.match(/filename="?([^";]+)"?/)
  const filename = match?.[1] ?? `daily-sales.${format}`

  downloadBlob(filename, response.data)
}

/**
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function fetchPaymentMixReport(options = {}) {
  const { data } = await api.get('/reports/payment-mix', { params: buildReportParams(options) })
  return data.data
}

/**
 * @param {'csv' | 'pdf'} format
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function exportPaymentMixReport(format, options = {}) {
  const params = { ...buildReportParams(options), format }

  const response = await api.get('/reports/payment-mix/export', {
    params,
    responseType: 'blob',
  })

  const disposition = response.headers['content-disposition'] ?? ''
  const match = disposition.match(/filename="?([^";]+)"?/)
  const filename = match?.[1] ?? `payment-mix.${format}`

  downloadBlob(filename, response.data)
}

/**
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function fetchServiceMixReport(options = {}) {
  const { data } = await api.get('/reports/service-mix', { params: buildReportParams(options) })
  return data.data
}

/**
 * @param {'csv' | 'pdf'} format
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function exportServiceMixReport(format, options = {}) {
  const params = { ...buildReportParams(options), format }

  const response = await api.get('/reports/service-mix/export', {
    params,
    responseType: 'blob',
  })

  const disposition = response.headers['content-disposition'] ?? ''
  const match = disposition.match(/filename="?([^";]+)"?/)
  const filename = match?.[1] ?? `service-mix.${format}`

  downloadBlob(filename, response.data)
}

/**
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function fetchRetailSalesReport(options = {}) {
  const { data } = await api.get('/reports/retail-sales', { params: buildReportParams(options) })
  return data.data
}

/**
 * @param {'csv' | 'pdf'} format
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function exportRetailSalesReport(format, options = {}) {
  const params = { ...buildReportParams(options), format }

  const response = await api.get('/reports/retail-sales/export', {
    params,
    responseType: 'blob',
  })

  const disposition = response.headers['content-disposition'] ?? ''
  const match = disposition.match(/filename="?([^";]+)"?/)
  const filename = match?.[1] ?? `retail-sales.${format}`

  downloadBlob(filename, response.data)
}

/**
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function fetchAppointmentReport(options = {}) {
  const { data } = await api.get('/reports/appointments', { params: buildReportParams(options) })
  return data.data
}

/**
 * @param {'csv' | 'pdf'} format
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function exportAppointmentReport(format, options = {}) {
  const params = { ...buildReportParams(options), format }

  const response = await api.get('/reports/appointments/export', {
    params,
    responseType: 'blob',
  })

  const disposition = response.headers['content-disposition'] ?? ''
  const match = disposition.match(/filename="?([^";]+)"?/)
  const filename = match?.[1] ?? `appointments.${format}`

  downloadBlob(filename, response.data)
}

/**
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function fetchOnlineBookingFunnelReport(options = {}) {
  const { data } = await api.get('/reports/online-booking-funnel', { params: buildReportParams(options) })
  return data.data
}

/**
 * @param {'csv' | 'pdf'} format
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function exportOnlineBookingFunnelReport(format, options = {}) {
  const params = { ...buildReportParams(options), format }

  const response = await api.get('/reports/online-booking-funnel/export', {
    params,
    responseType: 'blob',
  })

  const disposition = response.headers['content-disposition'] ?? ''
  const match = disposition.match(/filename="?([^";]+)"?/)
  const filename = match?.[1] ?? `online-booking-funnel.${format}`

  downloadBlob(filename, response.data)
}

/**
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function fetchZReportHistoricalReport(options = {}) {
  const { data } = await api.get('/reports/z-reports', { params: buildReportParams(options) })
  return data.data
}

/**
 * @param {'csv' | 'pdf'} format
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function exportZReportHistoricalReport(format, options = {}) {
  const params = { ...buildReportParams(options), format }

  const response = await api.get('/reports/z-reports/export', {
    params,
    responseType: 'blob',
  })

  const disposition = response.headers['content-disposition'] ?? ''
  const match = disposition.match(/filename="?([^";]+)"?/)
  const filename = match?.[1] ?? `z-reports.${format}`

  downloadBlob(filename, response.data)
}

/**
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function fetchInventoryReport(options = {}) {
  const { data } = await api.get('/reports/inventory', { params: buildReportParams(options) })
  return data.data
}

/**
 * @param {'csv' | 'pdf'} format
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function exportInventoryReport(format, options = {}) {
  const params = { ...buildReportParams(options), format }

  const response = await api.get('/reports/inventory/export', {
    params,
    responseType: 'blob',
  })

  const disposition = response.headers['content-disposition'] ?? ''
  const match = disposition.match(/filename="?([^";]+)"?/)
  const filename = match?.[1] ?? `inventory.${format}`

  downloadBlob(filename, response.data)
}

/**
 * @param {{ period?: string, from?: string, to?: string, compare?: string, birthdayDays?: number }} [options]
 */
export async function fetchCrmReport(options = {}) {
  const params = buildReportParams(options)
  if (options.birthdayDays) params.birthday_days = options.birthdayDays

  const { data } = await api.get('/reports/crm', { params })
  return data.data
}

/**
 * @param {'csv' | 'pdf'} format
 * @param {{ period?: string, from?: string, to?: string, compare?: string, birthdayDays?: number }} [options]
 */
export async function exportCrmReport(format, options = {}) {
  const params = { ...buildReportParams(options), format }
  if (options.birthdayDays) params.birthday_days = options.birthdayDays

  const response = await api.get('/reports/crm/export', {
    params,
    responseType: 'blob',
  })

  const disposition = response.headers['content-disposition'] ?? ''
  const match = disposition.match(/filename="?([^";]+)"?/)
  const filename = match?.[1] ?? `crm.${format}`

  downloadBlob(filename, response.data)
}

/**
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function fetchVatSummaryReport(options = {}) {
  const { data } = await api.get('/reports/vat-summary', { params: buildReportParams(options) })
  return data.data
}

/**
 * @param {'csv' | 'pdf'} format
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function exportVatSummaryReport(format, options = {}) {
  const params = { ...buildReportParams(options), format }

  const response = await api.get('/reports/vat-summary/export', {
    params,
    responseType: 'blob',
  })

  const disposition = response.headers['content-disposition'] ?? ''
  const match = disposition.match(/filename="?([^";]+)"?/)
  const filename = match?.[1] ?? `vat-summary.${format}`

  downloadBlob(filename, response.data)
}

/**
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function fetchVoidDiscountAuditReport(options = {}) {
  const { data } = await api.get('/reports/void-discount-audit', { params: buildReportParams(options) })
  return data.data
}

/**
 * @param {'csv' | 'pdf'} format
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function exportVoidDiscountAuditReport(format, options = {}) {
  const params = { ...buildReportParams(options), format }

  const response = await api.get('/reports/void-discount-audit/export', {
    params,
    responseType: 'blob',
  })

  const disposition = response.headers['content-disposition'] ?? ''
  const match = disposition.match(/filename="?([^";]+)"?/)
  const filename = match?.[1] ?? `void-discount-audit.${format}`

  downloadBlob(filename, response.data)
}

/**
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} options
 */
export async function fetchReportFilters(options = {}) {
  const { data } = await api.get('/reports/filters', { params: buildReportParams(options) })
  return data.data
}

/**
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function fetchTipReport(options = {}) {
  const { data } = await api.get('/reports/tips', { params: buildReportParams(options) })
  return data.data
}

/**
 * @param {'csv' | 'pdf'} format
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function exportTipReport(format, options = {}) {
  const params = { ...buildReportParams(options), format }

  const response = await api.get('/reports/tips/export', {
    params,
    responseType: 'blob',
  })

  const disposition = response.headers['content-disposition'] ?? ''
  const match = disposition.match(/filename="?([^";]+)"?/)
  const filename = match?.[1] ?? `tips.${format}`

  downloadBlob(filename, response.data)
}

/**
 * @param {'week' | 'month' | 'last_month' | 'today' | 'year' | 'custom'} period
 * @returns {{ from: string, to: string }}
 */
export function reportPeriodBounds(period) {
  return resolveReportPeriodBounds(period)
}

/**
 * @param {string | number | null | undefined} amount
 */
export function formatReportMoney(amount) {
  const value = Number(amount ?? 0)
  if (Number.isNaN(value)) return '0.00'
  return value.toFixed(2)
}

/**
 * @param {string | null | undefined} isoDate
 */
export function formatReportDate(isoDate) {
  if (!isoDate) return '—'
  const date = new Date(isoDate)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString()
}

/**
 * @param {string | null | undefined} isoDateTime
 */
export function formatReportDateTime(isoDateTime) {
  if (!isoDateTime) return '—'
  const date = new Date(isoDateTime)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString()
}

/**
 * @param {number | null | undefined} days
 */
export function formatDaysUntilExpiry(days) {
  if (days === null || days === undefined) return '—'
  if (days === 0) return 'Today'
  if (days === 1) return '1 day'
  return `${days} days`
}

/**
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} options
 * @returns {Record<string, string>}
 */
function buildReportParams(options) {
  const params = {}
  if (options.period) params.period = options.period
  if (options.from) params.from = options.from
  if (options.to) params.to = options.to
  if (options.compare) params.compare = options.compare
  return params
}

/**
 * @param {string} filename
 * @param {Blob} blob
 */
/**
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function fetchGatewayTransactionReport(options = {}) {
  const { data } = await api.get('/reports/gateway-transactions', { params: buildReportParams(options) })
  return data.data
}

/**
 * @param {'csv' | 'pdf'} format
 * @param {{ period?: string, from?: string, to?: string, compare?: string }} [options]
 */
export async function exportGatewayTransactionReport(format, options = {}) {
  const params = { ...buildReportParams(options), format }

  const response = await api.get('/reports/gateway-transactions/export', {
    params,
    responseType: 'blob',
  })

  const disposition = response.headers['content-disposition'] ?? ''
  const match = disposition.match(/filename="?([^";]+)"?/)
  const filename = match?.[1] ?? `gateway-transactions.${format}`

  downloadBlob(filename, response.data)
}

/**
 * @param {{ period?: string, from?: string, to?: string }} [options]
 */
export async function fetchBranchComparisonReport(options = {}) {
  const { data } = await api.get('/reports/branch-comparison', { params: buildReportParams(options) })
  return data.data
}

/**
 * @param {'csv' | 'pdf'} format
 * @param {{ period?: string, from?: string, to?: string }} [options]
 */
export async function exportBranchComparisonReport(format, options = {}) {
  const params = { ...buildReportParams(options), format }

  const response = await api.get('/reports/branch-comparison/export', {
    params,
    responseType: 'blob',
  })

  const disposition = response.headers['content-disposition'] ?? ''
  const match = disposition.match(/filename="?([^";]+)"?/)
  const filename = match?.[1] ?? `branch-comparison.${format}`

  downloadBlob(filename, response.data)
}

function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

/**
 * @param {{ report_key: string, frequency: string, emails: string[] }} payload
 */
export async function scheduleReportEmail(payload) {
  const { data } = await api.post('/reports/schedules', payload)
  return data.data
}

/**
 * @returns {Promise<{ schedules: Array<Record<string, unknown>> }>}
 */
export async function fetchReportSchedules() {
  const { data } = await api.get('/reports/schedules')
  return data.data
}
